import { useCallback, useRef, useState } from "react";

export const GRID_COL = {
	SUPPLIER: 0,
	PART_TYPE: 1,
	DEADLINE: 2,
	PRICE: 3,
} as const;

export const AUTOFILL_IDS = {
	supplier: "autofill-id-supplier-dropdown-paginated-supplier",
	partType: "autofill-id-buyerManualAnswer-select-partType",
	deadline: "autofill-id-buyerManualAnswer-txt-deadline",
};

export type GridColIndex = (typeof GRID_COL)[keyof typeof GRID_COL];

const TOTAL_COLS = 4;

function getInputId(row: number, col: GridColIndex): string {
	switch (col) {
		case GRID_COL.SUPPLIER:
			return `${row}-id-buyerManualAnswer-dropdown-paginated-parts.${row}.supplier`;
		case GRID_COL.PART_TYPE:
			return `${row}-id-buyerManualAnswer-select-partType`;
		case GRID_COL.DEADLINE:
			return `${row}-id-buyerManualAnswer-txt-deadline`;
		case GRID_COL.PRICE:
			return `${row}-id-buyerManualAnswer-txt-price`;
	}
}

function focusCell(row: number, col: GridColIndex) {
	const id = getInputId(row, col);
	document.getElementById(id)?.focus();
}

export function useGridTabNavigation(totalRows: number) {
	const [selectedCol, setSelectedCol] = useState<GridColIndex | null>(null);

	const selectedColRef = useRef(selectedCol);
	selectedColRef.current = selectedCol;

	const totalRowsRef = useRef(totalRows);
	totalRowsRef.current = totalRows;

	const handleGridTab = useCallback((e: React.KeyboardEvent, row: number, col: GridColIndex) => {
		if (e.key !== "Tab") return;

		const currentSelectedCol = selectedColRef.current;
		const currentTotalRows = totalRowsRef.current;

		if (currentSelectedCol !== null) {
			e.preventDefault();
			const nextRow = e.shiftKey ? (row - 1 + currentTotalRows) % currentTotalRows : (row + 1) % currentTotalRows;
			focusCell(nextRow, currentSelectedCol);
			return;
		}

		if (e.shiftKey) {
			if (col - 1 >= 0) {
				e.preventDefault();
				focusCell(row, (col - 1) as GridColIndex);
			} else if (row - 1 >= 0) {
				e.preventDefault();
				focusCell(row - 1, (TOTAL_COLS - 1) as GridColIndex);
			}
		} else {
			if (col + 1 < TOTAL_COLS) {
				e.preventDefault();
				focusCell(row, (col + 1) as GridColIndex);
			} else if (row + 1 < currentTotalRows) {
				e.preventDefault();
				focusCell(row + 1, GRID_COL.SUPPLIER);
			}
		}
	}, []);

	const toggleSelectedCol = useCallback((col: GridColIndex) => {
		setSelectedCol((prev) => {
			const next = prev === col ? null : col;
			if (next !== null) {
				setTimeout(() => focusCell(0, next), 0);
			}
			return next;
		});
	}, []);

	const clearSelectedCol = useCallback(() => {
		setSelectedCol(null);
	}, []);

	return {
		selectedCol,
		toggleSelectedCol,
		clearSelectedCol,
		handleGridTab,
	};
}
